import React from 'react';
import Layout from '../layout/Layout';
import CtaBand from '../layout/CtaBand';
import Section from '../ui/Section';
import PageHero from '../ui/PageHero';
import Sparkle from '../ui/Sparkle';
import Reveal from '../ui/Reveal';
import { blogPosts } from '../../content/library';

const meta = (p) => [p.date, p.readMinutes && `${p.readMinutes} min read`, p.author].filter(Boolean).join(' · ');

const Arrow = ({ sm }) => (
  <svg width={sm ? 16 : 18} height={sm ? 16 : 18} viewBox="0 0 24 24" fill="none" aria-hidden="true" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// `posts` (static + DB-backed, merged server-side) is passed by the controller.
// Falls back to the build-time static posts when rendered without props.
const Blog = ({ posts }) => {
  const list = Array.isArray(posts) ? posts : blogPosts;
  const [featured, ...rest] = list;
  return (
    <Layout>
      <PageHero tint="lilac" eyebrow="The Shopstar blog" title="Learn how to sell like a pro." lead="Tips, guides and stories to help you start, manage and grow your online store." />

      {featured && (
        <Section tone="white" pad="md">
          <a href={`/post/${featured.slug}`} className="group grid lg:grid-cols-[1.1fr_0.9fr] bg-cream border border-line rounded-4xl overflow-hidden hover:-translate-y-1 hover:shadow-card transition-all">
            <div className="relative min-h-[280px] bg-cream">
              {featured.heroImage && <img src={featured.heroImage} alt={featured.title} loading="eager" className="absolute inset-0 w-full h-full object-cover" />}
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-ink/80 backdrop-blur text-white text-xs font-bold px-3.5 py-1.5 rounded-full"><Sparkle size={14} color="#ffb442" /> In the spotlight</span>
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <span className="text-xs font-bold uppercase tracking-wide text-ink mb-3">{meta(featured)}</span>
              <h2 className="text-h2 font-extrabold text-ink mb-4">{featured.title}</h2>
              <p className="text-body mb-6">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 font-bold text-ink group-hover:text-amber">Read more <Arrow /></span>
            </div>
          </a>
        </Section>
      )}

      <Section tone="cream" pad="lg">
        <h2 className="text-h2 font-extrabold text-ink mb-10">Learn more about selling online</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 60}>
              <a href={`/post/${p.slug}`} className="group flex flex-col h-full bg-white border border-line rounded-3xl overflow-hidden hover:-translate-y-1.5 hover:shadow-card transition-all">
                <div className="aspect-[16/10] bg-cream overflow-hidden">{p.heroImage && <img src={p.heroImage} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}</div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-ink mb-3">{meta(p)}</span>
                  <h3 className="text-xl font-extrabold text-ink mb-3 leading-snug">{p.title}</h3>
                  <p className="text-body mb-5 flex-1 line-clamp-3">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-2 font-bold text-ink group-hover:text-amber">Read more <Arrow sm /></span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </Section>

      <CtaBand title="Less reading, more selling." text="Put these tips into practice. Start your free 14-day trial today." />
    </Layout>
  );
};

export default Blog;
